/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { isEqual as _isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Switch,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';

import { Namespaces } from '../../../i18n';
import { useBackupsConfigureStyles } from '../../../styles/backupsConfigure';
import { useFormStyles } from '../../../styles/form';
import { ClusterBackupConfiguration } from '../../../types/cluster/ClusterBackupConfiguration';
import { formatBackupDateTimeWithTimezone } from '../../../utils/formatBackupDateTimeWithTimezone';
import { dateToSchedule } from '../../../utils/scheduleUtils';
import { CreateClusterValues } from '../CreateClusterOverlay.types';

type CreateClusterConfigureBackupProps<Values> = FormikProps<Values> & object;

export function CreateClusterStepConfigureBackup<Values extends CreateClusterValues>(
  props: CreateClusterConfigureBackupProps<Values>,
) {
  const { values, setFieldValue, errors, setValues, validateForm } = props;
  const { t } = useTranslation([Namespaces.Cluster, Namespaces.CreateCluster]);
  const classes = useBackupsConfigureStyles();
  const formClasses = useFormStyles();

  useEffect(() => {
    void validateForm();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleLogical = useMemo<string[]>(() => {
    return values.backupConfigurationLogicalSchedule.split(' ') || [''];
  }, [values.backupConfigurationLogicalSchedule]);

  const scheduleContinuous = useMemo<string[]>(() => {
    return values.backupConfigurationContinuousSchedule.split(' ') || [''];
  }, [values.backupConfigurationContinuousSchedule]);

  const scheduleTimeLogical = useMemo(() => {
    const currentDate = new Date();
    const result = DateTime.fromObject({
      year: currentDate.getUTCFullYear(),
      month: currentDate.getUTCMonth() + 1,
      day: currentDate.getUTCDate(),
      hour: +scheduleLogical[1],
      minute: +scheduleLogical[0],
    });
    return result;
  }, [scheduleLogical]);

  const scheduleTimeContinuous = useMemo(() => {
    const currentDate = new Date();
    return DateTime.fromObject({
      year: currentDate.getUTCFullYear(),
      month: currentDate.getUTCMonth() + 1,
      day: currentDate.getUTCDate(),
      hour: +scheduleContinuous[1],
      minute: +scheduleContinuous[0],
    });
  }, [scheduleContinuous]);

  const nextBackupUtc = useCallback(
    (schedule: string[]) =>
      formatBackupDateTimeWithTimezone({
        utc: true,
        schedule: { minutes: +schedule[0], hours: +schedule[1] },
      }),
    [],
  );

  const nextBackupLocal = useCallback(
    (schedule: string[]) =>
      formatBackupDateTimeWithTimezone({
        schedule: { minutes: +schedule[0], hours: +schedule[1] },
      }),
    [],
  );

  const nextBackupLogicalLocal = useMemo(() => nextBackupLocal(scheduleLogical), [nextBackupLocal, scheduleLogical]);
  const nextBackupLogicalUtc = useMemo(() => nextBackupUtc(scheduleLogical), [nextBackupUtc, scheduleLogical]);
  const nextBackupContinuousLocal = useMemo(
    () => nextBackupLocal(scheduleContinuous),
    [nextBackupLocal, scheduleContinuous],
  );
  const nextBackupContinuousUtc = useMemo(() => nextBackupUtc(scheduleContinuous), [nextBackupUtc, scheduleContinuous]);

  const isDirty = useMemo<boolean>(() => {
    if (!values.sourceClusterBackupConfiguration) {
      return false;
    }
    const currentConfig: ClusterBackupConfiguration = {
      logical: {
        enabled: values.backupConfigurationLogicalEnabled,
        schedule: values.backupConfigurationLogicalSchedule,
        daysToRetain: values.backupConfigurationLogicalDaysToRetain
          ? +values.backupConfigurationLogicalDaysToRetain
          : 0,
      },
      continuous: {
        enabled: values.backupConfigurationContinuousEnabled,
        schedule: values.backupConfigurationContinuousSchedule,
        daysToRetain: values.backupConfigurationContinuousDaysToRetain
          ? +values.backupConfigurationContinuousDaysToRetain
          : 0,
      },
    };
    return !_isEqual(values.sourceClusterBackupConfiguration, currentConfig);
  }, [
    values.backupConfigurationContinuousDaysToRetain,
    values.backupConfigurationContinuousEnabled,
    values.backupConfigurationContinuousSchedule,
    values.backupConfigurationLogicalDaysToRetain,
    values.backupConfigurationLogicalEnabled,
    values.backupConfigurationLogicalSchedule,
    values.sourceClusterBackupConfiguration,
  ]);

  const onResetClick = () => {
    if (!values.sourceClusterBackupConfiguration) {
      return;
    }
    const { sourceClusterBackupConfiguration } = values;
    void setValues({
      ...values,
      backupConfigurationLogicalEnabled: sourceClusterBackupConfiguration.logical.enabled,
      backupConfigurationLogicalSchedule: sourceClusterBackupConfiguration.logical.schedule,
      backupConfigurationLogicalDaysToRetain: sourceClusterBackupConfiguration.logical.daysToRetain,
      backupConfigurationContinuousEnabled: sourceClusterBackupConfiguration.continuous.enabled,
      backupConfigurationContinuousSchedule: sourceClusterBackupConfiguration.continuous.schedule,
      backupConfigurationContinuousDaysToRetain: sourceClusterBackupConfiguration.continuous.daysToRetain,
    });
  };

  const { backupConfigurationContinuousEnabled, backupConfigurationLogicalEnabled } = values;

  const handleScheduleTimeLogicalChange = useCallback(
    (newValue: DateTime | null) => {
      if (newValue) {
        const schedule = dateToSchedule({ date: newValue.toJSDate(), m: true, h: true });
        setFieldValue('backupConfigurationLogicalSchedule', schedule).catch(console.error);
      }
    },
    [setFieldValue],
  );

  const handleScheduleTimeContinuousChange = useCallback(
    (newValue: DateTime | null) => {
      if (newValue) {
        const schedule = dateToSchedule({ date: newValue.toJSDate(), m: true, h: true });
        setFieldValue('backupConfigurationContinuousSchedule', schedule).catch(console.error);
      }
    },
    [setFieldValue],
  );

  return (
    <>
      <div className={classes.configureBackupLabel}>{t(`${Namespaces.CreateCluster}:parameters.configure_backup`)}</div>
      <Grid item classes={{ root: 'd-flex' }}>
        <div>
          <Grid item classes={{ root: 'd-flex' }}>
            <Switch
              checked={backupConfigurationContinuousEnabled}
              onChange={event => {
                setFieldValue('backupConfigurationContinuousEnabled', event.target.checked).catch(console.error);
              }}
              autoFocus
              sx={{ mr: '20px' }}
            />
            <Typography variant={'h3'} classes={{ root: classes.heading }}>
              {t(`${Namespaces.CreateCluster}:parameters.clone_continuous_backup_configuration`)}
            </Typography>
          </Grid>
          <Grid item classes={{ root: 'd-flex' }} sx={{ mt: '16px' }}>
            <FormControl className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}>
              <TimePicker
                label={t('details.backups.backups_time')}
                value={scheduleTimeContinuous}
                ampm={false}
                onChange={handleScheduleTimeContinuousChange}
                disabled={!backupConfigurationContinuousEnabled}
              />
            </FormControl>
            <FormControl
              className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}
              error={!!errors.backupConfigurationContinuousDaysToRetain}
            >
              <InputLabel>{t('details.backups.retention_period')}</InputLabel>
              <OutlinedInput
                label={t('details.backups.retention_period')}
                name="backupConfigurationContinuousDaysToRetain"
                error={!!errors.backupConfigurationContinuousDaysToRetain}
                fullWidth
                type="text"
                value={values.backupConfigurationContinuousDaysToRetain}
                endAdornment={<InputAdornment position="start">{t('details.backups.days')}</InputAdornment>}
                onChange={e => {
                  setFieldValue('backupConfigurationContinuousDaysToRetain', e.currentTarget.value).catch(
                    console.error,
                  );
                }}
                disabled={!backupConfigurationContinuousEnabled}
              />
              {!!errors.backupConfigurationContinuousDaysToRetain && (
                <FormHelperText>{t('details.backups.retention_period_not_valid')}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Collapse in={backupConfigurationContinuousEnabled}>
            <Divider component="div" sx={{ mt: '16px', mb: '16px' }} />
            <Typography variant={'h3'} classes={{ root: classes.h3 }}>
              {t('details.backups.next_continuous_backup_scheduled')}
            </Typography>
            <div className={classes.timeContainer}>
              <div style={{ marginRight: '3px' }}>
                <Typography className={classes.timeLabel}>{t('details.backups.server_time')}:</Typography>
                <Typography className={classes.timeLabel}>{t('details.backups.your_local_time')}:</Typography>
              </div>
              <div>
                <Typography className={classes.timeLabel}>{nextBackupContinuousUtc}</Typography>
                <Typography className={classes.timeLabel}>{nextBackupContinuousLocal}</Typography>
              </div>
            </div>
          </Collapse>
        </div>
      </Grid>
      <Grid item classes={{ root: 'd-flex' }} sx={{ marginTop: '45px' }}>
        <div>
          <Grid item classes={{ root: 'd-flex' }}>
            <Switch
              checked={backupConfigurationLogicalEnabled}
              onChange={event => {
                setFieldValue('backupConfigurationLogicalEnabled', event.target.checked).catch(console.error);
              }}
              sx={{ mr: '20px' }}
              data-cy="create-cluster-logical-backup-switch"
            />
            <Typography variant={'h3'} classes={{ root: classes.heading }}>
              {t(`${Namespaces.CreateCluster}:parameters.clone_logical_backup_configuration`)}
            </Typography>
          </Grid>
          <Grid item classes={{ root: 'd-flex' }} sx={{ mt: '16px' }}>
            <FormControl className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}>
              <div data-cy="create-cluster-logical-backup-schedule">
                <TimePicker
                  label={t('details.backups.backups_time')}
                  value={scheduleTimeLogical}
                  ampm={false}
                  onChange={handleScheduleTimeLogicalChange}
                  disabled={!backupConfigurationLogicalEnabled}
                />
              </div>
            </FormControl>
            <FormControl
              className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}
              error={!!errors.backupConfigurationLogicalDaysToRetain}
            >
              <InputLabel>{t('details.backups.retention_period')}</InputLabel>
              <OutlinedInput
                label={t('details.backups.retention_period')}
                error={!!errors.backupConfigurationLogicalDaysToRetain}
                fullWidth
                type="text"
                value={values.backupConfigurationLogicalDaysToRetain}
                endAdornment={<InputAdornment position="start">{t('details.backups.days')}</InputAdornment>}
                onChange={e => {
                  setFieldValue('backupConfigurationLogicalDaysToRetain', e.currentTarget.value).catch(console.error);
                }}
                disabled={!backupConfigurationLogicalEnabled}
                data-cy="create-cluster-logical-backup-retention"
              />
              {!!errors.backupConfigurationLogicalDaysToRetain && (
                <FormHelperText>{t('details.backups.retention_period_not_valid')}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Collapse in={backupConfigurationLogicalEnabled}>
            <Divider component="div" sx={{ mt: '16px', mb: '16px' }} />
            <div>
              <Typography variant={'h3'} classes={{ root: classes.h3 }}>
                {t('details.backups.next_logical_backup_scheduled')}
              </Typography>
              <div className={classes.timeContainer}>
                <div style={{ marginRight: '3px' }}>
                  <Typography className={classes.timeLabel}>{t('details.backups.server_time')}:</Typography>
                  <Typography className={classes.timeLabel}>{t('details.backups.your_local_time')}:</Typography>
                </div>
                <div>
                  <Typography className={classes.timeLabel}>{nextBackupLogicalUtc}</Typography>
                  <Typography className={classes.timeLabel}>{nextBackupLogicalLocal}</Typography>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </Grid>
      <div>
        {isDirty && (
          <Button onClick={onResetClick} variant="contained">
            {t(`${Namespaces.CreateCluster}:buttons.reset_backup_config`)}
          </Button>
        )}
      </div>
    </>
  );
}
